<template>
  <v-row align="center" dense justify="center">
    <v-col cols="10" sm="4" style="text-align:left">
      <p class="name">{{ item.name }}
      </p>

      <p class="price mb-1">
        <span class="mr-4">Kaufpreis</span>
        <span class="price-value" v-if="item.latestPrice">{{ item.latestPrice.price | finCurrency }}</span>
        <span class="price-value" v-else>-</span>
      </p>
      <p class="stockinfo" v-if="item.latestPrice">
        <span>Börse {{ item.latestPrice.exchangeName }}; </span>
        {{ item.latestPrice.timestamp | dayAndHourAndYearShort }} Uhr
      </p>
    </v-col>

    <v-col :class="$vuetify.breakpoint.xs ? 'd-inline-flex' : 'd-inline-flex pt-6'" cols="6" sm="4"
           style="flex-grow: 2;">
      <template v-if="maxCount!==null">
        <span class="slider-value">{{ sliderValue }}%</span>
        <v-slider v-model="sliderValue"
                  :color="$finColors.primaryDark"
                  :max="100"
                  :min="0"
                  :disabled="!item.latestPrice.price"
                  :track-color="$finColors.coolGrey"
                  @input="changeSlider"/>
      </template>
    </v-col>
    <v-col :class="$vuetify.breakpoint.xs ? '' : 'pt-6'" cols="6" sm="4">
      <div style="float: right">
        <div class="d-inline-flex">
          <v-icon v-if="maxCount!=null" :color="$finColors.lightGrayBlue" class="mr-6">mdi-equal</v-icon>
          <v-text-field :key="'number_'+domRepaintKey"
                        v-model="count"
                        background-color="white"
                        class="mr-4 count-input"
                        hide-details
                        outlined
                        :disabled="!item.latestPrice.price"
                        style="max-width: 70px;"
                        type="number"/>
          <span :style="{lineHeight: '40px', color: $finColors.coolGrey}">Stück</span>
          <v-btn @click="removeItem" class="ml-12" icon v-if="$vuetify.breakpoint.smAndUp">
            <v-icon :color="$finColors.coolGrey">icon-delete</v-icon>
          </v-btn>

        </div>
        <p class="investment-calc" :style="{margin: '0px', color: $finColors.paleRed}" v-if="count === 0">Anzahl > 0</p>
        <p v-if="count > 0" :class="{'mt-3' : count !== 0}" class="investment-calc mb-0">entspricht
          {{ price | finCurrency }}</p>
      </div>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'FinPortfolioDistributionSlider',
  props: {
    /** The count of shares */
    value: {
      type: Number,
      default: 0
    },
    /** The maximum number to select */
    maxCount: {
      type: Number,
      default: null
    },
    /** The investment item to display */
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      workingData: 0,
      domRepaintKey: 1,
      sliderValue: this.maxCount ? Math.round(100 / this.maxCount * this.value) : null
    }
  },
  methods: {
    removeItem() {
      /**
       * Event triggered when clicking the recycle bin button.
       *
       * @event remove-item
       * @type {Object} - the object to be removed
       */
      this.$emit('remove-item', this.item)
    },
    changeSlider(val) {
      this.count = Math.round(this.maxCount / 100 * val)
    },
  },
  computed: {
    count: {
      get() {
        return this.value
      },
      set(val) {
        let num = parseInt(val, 10)
        num = isNaN(num) ? 0 : num
        /**
         * Event triggered when changing the count via slider or input field.
         *
         * @event input
         * @type {Number} - the new count
         */
        this.$emit('input', num)
      }
    },
    price() {
      return this.count * this.item.latestPrice.price
    }
  },
  watch: {
    value(val) {
      this.sliderValue = Math.round(100 / this.maxCount * val)
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../../styles/variables.scss";

.name {
  font-family: $body-font-family;
  font-size: 24px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.price {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.39;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.price-value {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.stockinfo {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-cool-grey;
}

.slider-value {
  font-family: $body-font-family;
  font-size: 24px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
  /* additional styling */
  padding-top: 10px;
  margin-right: 10px;
}

.subline {
  font-family: $body-font-family;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

::v-deep .v-slider__track-container {
  height: 3px;
}

::v-deep .v-slider__thumb:before {
  -webkit-transform: scale(0.8) !important;
  transform: scale(0.8) !important;
  color: #ffffff;
  box-shadow: $fin-color-shadow;
  border: 1px solid $fin-color-light-sky-blue;
}

::v-deep .v-slider__thumb-container .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb-container:hover .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb:after {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: $fin-color-primary-dark;
  opacity: 1;
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}

.investment-calc {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

::v-deep .v-input__slot {
  height: 40px !important;
  min-height: 40px !important;
}

.text-gray {
  color: $fin-color-accent-gray
}

.count-input {
  min-width: 55px
}
</style>
